import React from 'react';
import { HeroSection } from '../../components/HeroSection';
import { motion } from 'framer-motion';
import { Scale, Building2, Users2, Stethoscope, Briefcase, GraduationCap } from 'lucide-react';
export function SystemsPower() {
  const systems = [{
    icon: <Stethoscope className="h-8 w-8" data-id="element-1684" />,
    title: 'Medical System',
    description: 'Healthcare providers pathologize fatness, deny care, and prescribe weight loss as cure-all.',
    examples: ['BMI as diagnostic tool', 'Weight-based dosing discrimination', 'Surgical denial based on size']
  }, {
    icon: <Briefcase className="h-8 w-8" data-id="element-1685" />,
    title: 'Employment',
    description: 'Hiring discrimination, wage gaps, and workplace harassment based on body size.',
    examples: ['Size-based hiring bias', 'Appearance clauses', 'Lack of accommodations']
  }, {
    icon: <Building2 className="h-8 w-8" data-id="element-1686" />,
    title: 'Built Environment',
    description: 'Physical spaces designed to exclude and punish fat bodies through hostile architecture.',
    examples: ['Narrow seating', 'Weight limits on furniture', 'Inaccessible public transit']
  }, {
    icon: <Scale className="h-8 w-8" data-id="element-1687" />,
    title: 'Legal System',
    description: 'No federal protections against size discrimination. Fat people can be legally fired, denied housing.',
    examples: ['No ADA coverage', 'Child custody bias', 'Insurance discrimination']
  }, {
    icon: <GraduationCap className="h-8 w-8" data-id="element-1688" />,
    title: 'Education',
    description: 'Schools enforce diet culture, enable bullying, and create hostile learning environments.',
    examples: ['BMI report cards', 'PE humiliation', 'Desk/chair exclusion']
  }, {
    icon: <Users2 className="h-8 w-8" data-id="element-1689" />,
    title: 'Social & Cultural',
    description: 'Media representation, dating discrimination, and everyday microaggressions normalize anti-fatness.',
    examples: ['Fat villain trope', 'Dating app filters', 'Public commentary']
  }];
  return <div className="min-h-screen bg-brand-cream" data-id="element-1690">
      <HeroSection title="Systems, Power & Anti-Fat Bias" subtitle="Understanding how anti-fatness operates at structural, institutional, and interpersonal levels." tag="Fat Liberation & Justice" bgColor="magenta" data-id="element-1691" />

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1692">
        <div className="prose prose-lg" data-id="element-1693">
          <p className="text-xl text-gray-700 leading-relaxed" data-id="element-1694">
            Anti-fat bias isn't just individual prejudice—it's a{' '}
            <strong className="text-brand-magenta" data-id="element-1695">system of oppression</strong>{' '}
            embedded in our institutions, policies, and cultural norms.
            Understanding these systems is the first step toward dismantling
            them.
          </p>
        </div>
      </section>

      {/* Systems Grid */}
      <section className="py-16 bg-white" data-id="element-1696">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1697">
          <h2 className="text-3xl font-black uppercase mb-12 text-brand-black" data-id="element-1698">
            Six Interconnected Systems
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1699">
            {systems.map((system, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.1
          }} viewport={{
            once: true
          }} className="border-2 border-brand-black bg-brand-cream p-6 hover:shadow-xl transition-shadow" data-id="element-1700">
                <div className="text-brand-magenta mb-4" data-id="element-1701">{system.icon}</div>
                <h3 className="text-xl font-black uppercase mb-3 text-brand-black" data-id="element-1702">
                  {system.title}
                </h3>
                <p className="text-gray-700 mb-4 text-sm leading-relaxed" data-id="element-1703">
                  {system.description}
                </p>
                <div className="space-y-2" data-id="element-1704">
                  <p className="text-xs font-bold uppercase text-gray-500 mb-2" data-id="element-1705">
                    Examples:
                  </p>
                  {system.examples.map((example, i) => <div key={i} className="flex items-start" data-id="element-1706">
                      <span className="text-brand-magenta mr-2" data-id="element-1707">•</span>
                      <span className="text-sm text-gray-600" data-id="element-1708">{example}</span>
                    </div>)}
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Power Analysis */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto" data-id="element-1709">
        <h2 className="text-3xl font-black uppercase mb-12 text-brand-black border-b-4 border-brand-magenta inline-block pb-2" data-id="element-1710">
          Who Benefits?
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-1711">
          <div className="bg-brand-magenta text-white p-8 rounded-sm" data-id="element-1712">
            <h3 className="text-2xl font-black uppercase mb-6" data-id="element-1713">
              The Diet Industry
            </h3>
            <p className="text-lg mb-4 opacity-90" data-id="element-1714">
              $72 billion annually in the US alone
            </p>
            <ul className="space-y-3 text-sm" data-id="element-1715">
              <li className="flex items-start" data-id="element-1716">
                <span className="mr-2" data-id="element-1717">→</span>
                <span data-id="element-1718">Weight loss programs, apps, and supplements</span>
              </li>
              <li className="flex items-start" data-id="element-1719">
                <span className="mr-2" data-id="element-1720">→</span>
                <span data-id="element-1721">Pharmaceutical companies pushing weight loss drugs</span>
              </li>
              <li className="flex items-start" data-id="element-1722">
                <span className="mr-2" data-id="element-1723">→</span>
                <span data-id="element-1724">Bariatric surgery industry</span>
              </li>
              <li className="flex items-start" data-id="element-1725">
                <span className="mr-2" data-id="element-1726">→</span>
                <span data-id="element-1727">"Wellness" rebranding of diet culture</span>
              </li>
            </ul>
          </div>

          <div className="bg-brand-navy text-white p-8 rounded-sm" data-id="element-1728">
            <h3 className="text-2xl font-black uppercase mb-6" data-id="element-1729">
              White Supremacy
            </h3>
            <p className="text-lg mb-4 opacity-90" data-id="element-1730">
              Anti-fatness is rooted in racism
            </p>
            <ul className="space-y-3 text-sm" data-id="element-1731">
              <li className="flex items-start" data-id="element-1732">
                <span className="mr-2" data-id="element-1733">→</span>
                <span data-id="element-1734">Body ideals based on European beauty standards</span>
              </li>
              <li className="flex items-start" data-id="element-1735">
                <span className="mr-2" data-id="element-1736">→</span>
                <span data-id="element-1737">Pathologization of Black and Indigenous bodies</span>
              </li>
              <li className="flex items-start" data-id="element-1738">
                <span className="mr-2" data-id="element-1739">→</span>
                <span data-id="element-1740">Eugenics history of BMI and weight science</span>
              </li>
              <li className="flex items-start" data-id="element-1741">
                <span className="mr-2" data-id="element-1742">→</span>
                <span data-id="element-1743">
                  Controlling Black women's bodies through "health" discourse
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Intersections */}
      <section className="py-20 bg-gray-50" data-id="element-1744">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1745">
          <h2 className="text-3xl font-black uppercase mb-8 text-brand-black" data-id="element-1746">
            Intersectional Impact
          </h2>
          <p className="text-lg text-gray-700 mb-8" data-id="element-1747">
            Anti-fat bias doesn't exist in isolation. It compounds with other
            systems of oppression:
          </p>

          <div className="space-y-6" data-id="element-1748">
            <div className="bg-white border-l-4 border-brand-magenta p-6" data-id="element-1749">
              <h4 className="font-bold text-lg mb-2" data-id="element-1750">Race + Fatness</h4>
              <p className="text-gray-700" data-id="element-1751">
                Fat Black people face both anti-Blackness and anti-fatness.
                Medical racism means less pain management, more coercion into
                weight loss, higher maternal mortality.
              </p>
            </div>

            <div className="bg-white border-l-4 border-brand-magenta p-6" data-id="element-1752">
              <h4 className="font-bold text-lg mb-2" data-id="element-1753">Disability + Fatness</h4>
              <p className="text-gray-700" data-id="element-1754">
                Fat disabled people are blamed for their disabilities. Mobility
                aids are denied. Fatness is treated as the "real" problem to
                solve.
              </p>
            </div>

            <div className="bg-white border-l-4 border-brand-magenta p-6" data-id="element-1755">
              <h4 className="font-bold text-lg mb-2" data-id="element-1756">
                Trans Identity + Fatness
              </h4>
              <p className="text-gray-700" data-id="element-1757">
                Trans fat people face additional barriers to gender-affirming
                care. Body size is weaponized to deny hormones and surgery.
              </p>
            </div>

            <div className="bg-white border-l-4 border-brand-magenta p-6" data-id="element-1758">
              <h4 className="font-bold text-lg mb-2" data-id="element-1759">Class + Fatness</h4>
              <p className="text-gray-700" data-id="element-1760">
                Poor fat people are blamed for their poverty. "Healthy" food is
                expensive. Weight loss is marketed as path to economic mobility.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>;
}